import React, { useEffect, useContext } from "react";
import { ThemeContext } from "styled-components";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { values, isEmpty, includes } from "ramda";
import {
  Box,
  FormattedAddress,
  Stack,
  Title,
  EditableList,
  Placeholder,
  util,
  constants
} from "@thecb/components";
import FormattedCreditCard from "/components/formatted-credit-card";
import FormattedBankAccount from "/components/formatted-bank-account";
import { format } from "formatted-input";
import {
  PHONE_RESOURCE,
  EMAIL_RESOURCE,
  ADDRESS_RESOURCE,
  CREDIT_CARD_KIND,
  BANK_ACCOUNT_KIND
} from "../../Profile.state";
import { RemovePaymentModal } from "./RemovePaymentModal";
import AlertBar from "/components/alert-bar";
import { configureEndpoint } from "/util/router-utils";
import {
  getCreditCardsForDisplay,
  getBankAccountsForDisplay
} from "../../Profile.selectors";
import { isInCustomerManagement } from "../../../../../../util/router-utils";

const ProfileWalletSettings = ({
  actions,
  resources,
  resourcesActions,
  autoPayData,
  alertBars,
  profileRouting,
  customerManagement
}) => {
  const { profileWalletSettingsAlertBar: alertBar } = alertBars;
  const {
    alertBars: { profileWalletSettingsAlertBar: alertBarActions }
  } = actions;
  const creditCards = getCreditCardsForDisplay(resources);
  const bankAccounts = getBankAccountsForDisplay(resources);
  useEffect(() => {
    if (resources.creditCards) {
      resourcesActions.checkPaymentExpiration();
    }
  }, [resources.creditCards]);
  const paymentInstruments = [...creditCards, ...bankAccounts];
  const autoPayMethods =
    Object.entries(autoPayData)
      ?.filter(
        ([key]) =>
          key !== "selectedAutoPaymentMethodId" &&
          key !== "termsAndConditionsAgreedTo"
      )
      .map(([, autoPayRecord]) => autoPayRecord?.paymentInstrument?.id) ?? [];
  const redirectLink = includes("accounts", profileRouting)
    ? configureEndpoint(customerManagement, "/profile/accounts")
    : configureEndpoint(customerManagement, "/profile/properties");
  const modalProps = {
    autoPayMethods,
    modalDestination: redirectLink,
    removePaymentMethod: resourcesActions.deactivatePaymentInstrument
  };

  const formatPhone = format(util.formats.phoneFormat);

  return (
    <Stack childGap="24px" role="main">
      {!isEmpty(alertBar.alerts) && (
        <AlertBar {...alertBar} {...alertBarActions} />
      )}
      <Stack childGap="0.5rem">
        <Title
          weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
          as="h1"
          variant="small"
        >
          Wallet
        </Title>
        <Title
          as="h2"
          weight={constants.fontWeights.FONT_WEIGHT_REGULAR}
          color={constants.colors.CHARADE_GREY}
          extraStyles="letter-spacing: 0.29px; font-size: 1.125rem;"
        >
          Payment Methods
        </Title>
        {isEmpty(paymentInstruments) ? (
          <Box
            background={constants.colors.WHITE}
            extraStyles={`box-shadow: 0px 2px 14px 0px ${constants.colors.ATHENS_GREY},
      0px 3px 8px 0px ${constants.colors.GHOST_GREY}; margin-bottom: 16px;`}
            key="placeholder-box"
          >
            <Placeholder
              isLink
              variant="large"
              destination="add-payment"
              text="Add a Payment Method"
              largeIcon="payments"
              key="placeholder"
              dataQa="add payment"
              isDisabled={isInCustomerManagement}
            />
          </Box>
        ) : (
          <Box padding="0 5px">
            <EditableList
              addItemDestination={configureEndpoint(
                customerManagement,
                "/profile/wallet/add-payment"
              )}
              canRemove={!customerManagement}
              useModal
              modal={RemovePaymentModal}
              modalProps={modalProps}
              canEdit={false}
              itemName="Payment Method"
              items={paymentInstruments}
              autoPayMethods={autoPayMethods}
              disablePlaceholder={isInCustomerManagement}
              renderItem={i => (
                <Stack>
                  {i.kind === BANK_ACCOUNT_KIND && (
                    <FormattedBankAccount
                      lastFour={i.lastFour}
                      accountType={i.accountType}
                      autoPay={autoPayMethods.some(
                        methodID => methodID === i.id
                      )}
                    />
                  )}
                  {i.kind === CREDIT_CARD_KIND && (
                    <FormattedCreditCard
                      lastFour={i.lastFour}
                      autoPay={autoPayMethods.some(
                        methodID => methodID === i.id
                      )}
                      expireDate={i.expireDate}
                      expirationStatus={i.expirationStatus}
                      type={i.cardType}
                    />
                  )}
                </Stack>
              )}
              qaPrefix="Payment Method"
            />
          </Box>
        )}
      </Stack>
      <Stack childGap="0.5rem">
        <Box padding="0 5px">
          <EditableList
            title="Mailing Address"
            as="h2"
            addItemDestination={configureEndpoint(
              customerManagement,
              "/profile/wallet/add-address"
            )}
            canRemove={false}
            getEditHref={item =>
              configureEndpoint(
                customerManagement,
                `/profile/wallet/edit-address/${item.id}`
              )
            }
            itemName="Mailing Address"
            canEdit={!customerManagement}
            renderItem={item => <FormattedAddress {...item} />}
            listItemSize={"big"}
            maxItems={1}
            items={values(resources[ADDRESS_RESOURCE]).map(a => ({
              id: a.id,
              street1: a.street1,
              street2: a.street2,
              city: a.city,
              country: a.country,
              stateProvince: a.stateProvince,
              zip: a.zip,
              isPrimary: a.isPrimary
            }))}
            qaPrefix="Address"
            disablePlaceholder={isInCustomerManagement}
          />
        </Box>
        <Box padding="0 5px">
          <EditableList
            canRemove={false}
            canEdit={!customerManagement}
            title="Phone Number"
            as="h2"
            addItemDestination={configureEndpoint(
              customerManagement,
              "/profile/wallet/add-phone"
            )}
            getEditHref={item =>
              configureEndpoint(
                customerManagement,
                `/profile/wallet/edit-phone/${item.id}`
              )
            }
            itemName="Phone Number"
            renderItem={phone => formatPhone(phone.value)}
            maxItems={1}
            items={values(resources[PHONE_RESOURCE])}
            qaPrefix="Phone Number"
            disablePlaceholder={isInCustomerManagement}
          />
        </Box>
        <Box padding="0 5px">
          <EditableList
            canRemove={false}
            title="Email for Receipts"
            as="h2"
            canEdit={!customerManagement}
            addItemDestination={configureEndpoint(
              customerManagement,
              "/profile/wallet/add-email"
            )}
            removeItem={resourcesActions.onRemoveEmail}
            getEditHref={item =>
              configureEndpoint(
                customerManagement,
                `/profile/wallet/edit-email/${item.id}`
              )
            }
            itemName="Email"
            renderItem={email => email.value}
            maxItems={1}
            items={values(resources[EMAIL_RESOURCE])}
            qaPrefix="Email"
            disablePlaceholder={isInCustomerManagement}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default connect(null, { push })(React.memo(ProfileWalletSettings));
